<template>
  <div class="period-range">
    <b-button
      v-b-tooltip.hover.top="'Mês anterior'"
      variant="outline-secondary"
      class="btn-icon period-range__step"
      aria-label="Mês anterior"
      :disabled="disabled"
      @click="andarMes(-1)"
    >
      <feather-icon
        icon="ChevronLeftIcon"
        size="16"
      />
    </b-button>
    <div class="period-range__field">
      <feather-icon
        icon="CalendarIcon"
        size="16"
        class="period-range__icon"
      />
      <flat-pickr
        :id="id"
        :value="value"
        class="form-control"
        :config="config"
        placeholder="Escolha o período"
        aria-label="Período"
        @input="$emit('input', $event)"
        @on-close="aoFecharCalendario"
      />
      <span class="period-range__tag">{{ rotuloDoPeriodo }}</span>
    </div>
    <b-button
      v-b-tooltip.hover.top="'Próximo mês'"
      variant="outline-secondary"
      class="btn-icon period-range__step"
      aria-label="Próximo mês"
      :disabled="disabled || !temProximoMes"
      @click="andarMes(1)"
    >
      <feather-icon
        icon="ChevronRightIcon"
        size="16"
      />
    </b-button>
  </div>
</template>

<script>
import { BButton, VBTooltip } from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
import ShortcutButtonsPlugin from 'shortcut-buttons-flatpickr'
import {
  PRESETS,
  periodLabel,
  presetKeyOf,
  presetRange,
  resolvePeriod,
  shiftMonth,
} from '@/modules/kpi/utils/kpiPeriod'

/**
 * O seletor de período do Faturômetro 2.0 (KpiTreeView), como componente:
 * setas de mês, etiqueta do período ("Este mês", "Abril 2026") e atalhos no
 * calendário. O v-model é a string do flatpickr em modo range,
 * "AAAA-MM-DD até AAAA-MM-DD".
 *
 * `change` sai quando há uma faixa completa escolhida: calendário fechado com
 * as duas datas, atalho ou seta. É o momento de buscar.
 */
export default {
  name: 'PeriodRangePicker',
  components: { BButton, flatPickr },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    value: {
      type: String,
      default: undefined,
    },
    id: {
      type: String,
      default: 'periodRange',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // O atalho que a pessoa clicou: desempata quando dois atalhos dão a
      // mesma faixa (ex.: "Este mês" e "Este trimestre" no primeiro mês dele).
      atalhoEscolhido: null,
      config: {
        altFormat: 'd/m/Y',
        altInput: true,
        mode: 'range',
        locale: Portuguese,
        plugins: [ShortcutButtonsPlugin({
          button: PRESETS.map(preset => ({
            label: preset.label,
            attributes: { class: 'period-range-calendar__preset' },
          })),
          onClick: (indice, fp) => this.aplicarAtalho(PRESETS[indice].key, fp),
        })],
        // O flatpickr pendura o popup no <body>; a classe limita o CSS dos
        // atalhos a este calendário.
        onReady: [(datas, texto, fp) => fp.calendarContainer.classList.add('period-range-calendar')],
        onOpen: [(datas, texto, fp) => this.marcarAtalhoAtivo(fp)],
      },
    }
  },
  computed: {
    rotuloDoPeriodo() {
      const { startDate, endDate } = resolvePeriod(this.value)
      return periodLabel(startDate, endDate, undefined, this.atalhoEscolhido)
    },
    temProximoMes() {
      return !!shiftMonth(resolvePeriod(this.value).endDate, 1)
    },
  },
  methods: {
    /**
     * O atalho escreve a faixa no próprio calendário e fecha: ao fechar, o
     * flatpickr devolve ao v-model a faixa que está nele, e o `change` sai por
     * aoFecharCalendario já com a faixa nova.
     */
    aplicarAtalho(chave, fp) {
      const { startDate, endDate } = presetRange(chave)
      this.atalhoEscolhido = chave
      fp.setDate([startDate, endDate], true)
      fp.close()
    },
    /** ‹ e ›: a competência inteira antes ou depois da que se está vendo. */
    andarMes(passo) {
      const faixa = shiftMonth(resolvePeriod(this.value).endDate, passo)
      if (!faixa) {
        return
      }
      this.atalhoEscolhido = null
      this.$emit('input', `${faixa.startDate} até ${faixa.endDate}`)
      this.$nextTick(() => this.$emit('change'))
    },
    /** Com uma data só (a pessoa desistiu no meio) não dispara a busca. */
    aoFecharCalendario(datas) {
      if (datas && datas.length === 2) {
        this.$nextTick(() => this.$emit('change'))
      }
    },
    /** Acende, no calendário, o atalho que descreve o período atual. */
    marcarAtalhoAtivo(fp) {
      const { startDate, endDate } = resolvePeriod(this.value)
      const ativo = presetKeyOf(startDate, endDate, undefined, this.atalhoEscolhido)
      const botoes = fp.calendarContainer.querySelectorAll('.period-range-calendar__preset')
      botoes.forEach((botao, indice) => {
        botao.classList.toggle('is-active', PRESETS[indice] && PRESETS[indice].key === ativo)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// Mesmo desenho do seletor do Faturômetro (KpiTreeView, .kpi-period*). As
// cores vêm dos tokens --kpi-* de style.scss, que o tema escuro redefine.
.period-range {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
}

// A moldura fica no wrapper; input e etiqueta dividem o espaço lado a lado,
// para a etiqueta nunca cobrir a data.
.period-range__field {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  min-width: 0;
  height: 2.714rem;
  border: 1px solid var(--kpi-input-border, #d8d6de);
  border-radius: 0.357rem;
  background: var(--kpi-input-bg, #fff);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.period-range__field:focus-within {
  border-color: #f26237;
  box-shadow: 0 3px 10px 0 rgba(34, 41, 47, 0.1);
}

.period-range__icon {
  flex: none;
  margin-left: 0.8rem;
  color: var(--kpi-ink-muted, #b9b9c3);
  pointer-events: none;
}

// O input visível é o `altInput` que o flatpickr cria fora do template.
.period-range__field ::v-deep .form-control {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  padding: 0 0.5rem 0 0.6rem;
  border: 0;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
}

.period-range__tag {
  flex: none;
  margin-right: 0.55rem;
  padding: 0.1rem 0.55rem;
  border-radius: 1rem;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  background: rgba(242, 98, 55, 0.12);
  color: var(--kpi-period-tag-fg, #ca390d);
  pointer-events: none;
}

.period-range__step {
  flex: none;
}
</style>

<style lang="scss">
// Tema do calendário do template (sem ele o popup abre branco e azul no meio
// do tema escuro).
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.period-range-calendar .shortcut-buttons-flatpickr-wrapper {
  display: block;
  padding: 0.65rem 0.6rem 0.75rem;
  border-top: 1px solid var(--kpi-line, #ebe9f1);
}

.period-range-calendar .shortcut-buttons-flatpickr-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.4rem;
}

.period-range-calendar .period-range-calendar__preset {
  height: 28px;
  margin: 0;
  padding: 0 0.75rem;
  border: 1px solid var(--kpi-line-strong, #d8d6de);
  border-radius: 14px;
  background: transparent;
  font-size: 0.8rem;
  color: inherit;
  cursor: pointer;

  &:hover {
    border-color: #f26237;
    color: #f26237;
  }

  &.is-active {
    border-color: #f26237;
    background: #f26237;
    color: #fff;
    font-weight: 600;
  }
}
</style>
